'use client'

import type { Session } from 'next-auth'
import { SessionProvider } from 'next-auth/react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, Suspense } from 'react'
import { usePostHog } from 'posthog-js/react'
import posthog from 'posthog-js'
import { PostHogProvider as PHProvider } from 'posthog-js/react'
import { useSession } from 'next-auth/react'

export default function Providers({
  session,
  children
}: {
  session: Session | null
  children: React.ReactNode
}) {
  return (
    <SessionProvider session={session}>
      <PostHogProvider>{children}</PostHogProvider>
    </SessionProvider>
  )
}

// PostHog provider component that initializes PostHog
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  // Initialize PostHog on component mount
  useEffect(() => {
    posthog.init('phc_25jDnkfARmlvaldQsFdkfSpCdFWsAlgCybhApv0SC2N', {
      persistence: 'localStorage',
      api_host: 'https://us.i.posthog.com',
      person_profiles: 'always', // use 'always' to create profiles for anonymous users as well
      capture_pageview: false, // Disable automatic pageview capture, as we capture manually,
      capture_pageleave: true
    })
  }, [])

  return (
    <PHProvider client={posthog}>
      <SuspendedPostHogPageView />
      <PostHogIdentify />
      {children}
    </PHProvider>
  )
}

// Component that tracks page views
function PostHogPageView() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const posthog = usePostHog()

  // Track pageviews
  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname
      if (searchParams.toString()) {
        url = url + '?' + searchParams.toString()
      }

      posthog.capture('$pageview', { $current_url: url })
    }
  }, [pathname, searchParams, posthog])

  return null
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  )
}

// Identify users in PostHog
function PostHogIdentify() {
  const { data: session } = useSession()
  const posthog = usePostHog()

  useEffect(() => {
    // When session changes and user is authenticated
    if (session?.user) {
      // Identify the user in PostHog
      posthog.identify(
        session.user.id || session.user.email || 'anonymous', // Use a unique ID
        {
          email: session.user.email,
          name: session.user.name
        }
      )
    } else {
      // Reset identity when user logs out
      posthog.reset()
    }
  }, [session, posthog])

  return null
}
